import { get } from "lodash";
import { isReservationV2Enabled } from "../../helpers/featureFlags";

/**
 * Fetches reservable tables twice in parallel: once with the default
 * `available_only: true` (only open slots) and once with `available_only: false`
 * (all slots, booked + open). Callers own their own loading/error handling.
 *
 * Hits /reservation/tables (v1) or /admin/v2/reservations/stores/:store_id/tables
 * (v2) depending on VITE_APP_RESERVATION_V2_ENABLED — same request/response
 * shape either way, just a different URL and store_id placement (body vs path).
 *
 * @param {object} params
 * @param {object} params.api - core("pu").get() api instance
 * @param {string} params.store_id
 * @param {string} params.reservation_date
 * @param {number} params.party_size
 * @param {boolean} [params.override_table_capacity] - v2-only admin override
 * @returns {Promise<{ reservableTables: object[], allReservableTables: object[] }>}
 */
export const fetchReservableTablesData = async ({
  api,
  store_id,
  reservation_date,
  party_size,
  override_reservable_days = false,
  override_table_capacity = false,
}) => {
  const v2 = isReservationV2Enabled();
  const url = v2
    ? `/admin/v2/reservations/stores/${store_id}/tables`
    : `/reservation/tables`;
  const baseData = {
    ...(v2 ? {} : { store_id }),
    reservation_date,
    party_size,
    override_reservable_days,
    ...(v2 && { override_table_capacity }),
  };

  const [availableRes, allRes] = await Promise.all([
    api.post({ url, data: baseData }),
    api.post({ url, data: { ...baseData, available_only: false } }),
  ]);

  return {
    reservableTables: get(availableRes, "data.result[0].tables", []),
    allReservableTables: get(allRes, "data.result[0].tables", []),
  };
};

/**
 * Builds the time-slot list for the Available Slots picker. Each slot carries a
 * `isFullyBooked` flag derived by diffing the full slot list (from the
 * `available_only: false` response) against the available slots (from the
 * default response).
 *
 * @param {object} params
 * @param {object[]} params.reservableTables - available-only tables
 * @param {object[]} params.allReservableTables - all tables from the result of fetchReservableTablesData with the payload `available_only: false`
 * @returns {{ value: string, isFullyBooked: boolean }[]}
 */
export const buildTimeSlots = ({
  reservableTables = [],
  allReservableTables = [],
}) => {
  const availableSlotsSet = new Set(
    reservableTables
      .flatMap((table) => table?.time_slots ?? [])
      .filter((e) => e),
  );

  // Fall back to the available-only union if the full list is empty (e.g. the
  // second request failed) so behavior degrades gracefully.
  const source = allReservableTables.length
    ? allReservableTables
    : reservableTables;

  const slots = [
    ...new Set(
      source.flatMap((table) => table?.time_slots ?? []).filter((e) => e),
    ),
  ]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({
      value,
      isFullyBooked: !availableSlotsSet.has(value),
    }));

  return slots;
};

/**
 * Check if the selected table is fully booked for the selected time slot.
 *
 * @param {string} tableId - selected table id
 * @param {string} ts - selected time slot (HH:mm)
 * @param {object[]} tables - table list from the response of available_only = true OR the 'reservableTables' of the method 'fetchReservableTablesData'
 * @returns {boolean}
 */
export const getIsTableFullyBooked = (tableId, ts, tables) => {
  if (!tableId || !ts || !Array.isArray(tables)) return false;
  return !tables
    .find((t) => t?.item?._id === tableId)
    ?.time_slots?.includes?.(ts);
};

/**
 * Unique experiences offered by a table at a given time, deduped by _id.
 * Shared by ReservationsConsole and ReservationsCalendarHeader, whose class
 * methods of the same name wrap this with their own state as the args.
 *
 * @param {object} params
 * @param {string} params.tableId
 * @param {object[]} params.allReservableTables - full (available + booked) table list
 * @param {string} params.reservationTime - selected time slot (HH:mm)
 * @returns {object[]}
 */
export const getTableUniqueExperience = ({
  tableId,
  allReservableTables = [],
  reservationTime,
}) => {
  if (!tableId) return [];
  const selectedTable = allReservableTables.find(
    (table) => table?.item?._id === tableId,
  );
  if (!selectedTable) return [];
  const experiencesForSelectedTableAndTime = (
    selectedTable?.experiences ?? []
  ).flatMap((wrapper) => {
    const isAvailable = wrapper?.time_slots?.includes?.(reservationTime);
    return isAvailable ? wrapper.items || [] : [];
  });
  if (!experiencesForSelectedTableAndTime) return [];
  return experiencesForSelectedTableAndTime.reduce((acc, current) => {
    const existing = acc.find((item) => item._id === current._id);
    if (!existing) {
      acc.push(current);
    }
    return acc;
  }, []);
};

/**
 * After a party-size change triggers a tables refetch, decides which of the
 * previously selected time / table / experience are still valid against the
 * fresh availability data. Cascades: time -> table -> experience, since the
 * UI itself is time-gated-table-gated-experience.
 *
 * @param {object} params
 * @param {object[]} params.reservableTables - available-only tables for the new party size
 * @param {object[]} params.allReservableTables - full table list for the new party size
 * @param {object} params.values - current form values (reservation_time, table, experience)
 * @returns {{ reservation_time: string|null, table: string|null, experience: string|null } | null}
 *   null when every current selection is still valid (nothing to change).
 */
export const getPrunedSelectionsAfterPartySizeChange = ({
  reservableTables = [],
  allReservableTables = [],
  values = {},
}) => {
  const { reservation_time, table, experience } = values;

  if (!reservation_time && !table && !experience) return null;

  const allTimeSlots = buildTimeSlots({
    reservableTables,
    allReservableTables,
  });
  const isTimeValid =
    !reservation_time ||
    allTimeSlots.some((slot) => slot.value === reservation_time);
  const nextReservationTime = isTimeValid ? reservation_time : null;

  const tablesForSelectedTime = nextReservationTime
    ? reservableTables.filter((t) =>
        t?.time_slots?.includes?.(nextReservationTime),
      )
    : [];
  const isTableValid =
    !table || tablesForSelectedTime.some((t) => t?.item?._id === table);
  const nextTable = isTableValid ? table : null;

  const uniqueExperiences = nextTable
    ? getTableUniqueExperience({
        tableId: nextTable,
        allReservableTables,
        reservationTime: reservation_time,
      })
    : [];
  const isExperienceValid =
    !experience ||
    (nextTable && uniqueExperiences.some((exp) => exp._id === experience));
  const nextExperience = isExperienceValid ? experience : null;

  if (
    nextReservationTime === reservation_time &&
    nextTable === table &&
    nextExperience === experience
  ) {
    return null;
  }

  return {
    reservation_time: nextReservationTime,
    table: nextTable,
    experience: nextExperience,
  };
};
